import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search } from 'lucide-react';
import toast from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentUser: any;
}

export function CreateFriendlyMatchModal({ isOpen, onClose, onSuccess, currentUser }: Props) {
  const [users, setUsers] = useState<any[]>([]);
  const [opponentId, setOpponentId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [scoreFormat, setScoreFormat] = useState('1 Set (Padrão)');
  const [scoreSets, setScoreSets] = useState([{ p1: '', p2: '' }]);
  const [winnerId, setWinnerId] = useState('');
  
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [court, setCourt] = useState('');

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
  const token = localStorage.getItem('tt_auth_token');

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (scoreFormat === '1 Set (Padrão)') setScoreSets([{ p1: '', p2: '' }]);
    else if (scoreFormat === 'Melhor de 3 Sets' || scoreFormat === '2 Sets + Supertiebreak') setScoreSets([{ p1: '', p2: '' }, { p1: '', p2: '' }, { p1: '', p2: '' }]);
    else if (scoreFormat === 'Melhor de 5 Sets') setScoreSets([{ p1: '', p2: '' }, { p1: '', p2: '' }, { p1: '', p2: '' }, { p1: '', p2: '' }, { p1: '', p2: '' }]);
  }, [scoreFormat]);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.filter((u: any) => u.id !== currentUser?.id));
      }
    } catch (e) {
      console.log('Error fetching users', e);
    }
  };

  if (!isOpen) return null;

  const filteredUsers = users.filter((u: any) => {
    const name = (u.name || '').normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    return name.includes(search.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!opponentId) {
      toast.error('Selecione um adversário.');
      return;
    }
    
    const hasScore = scoreSets.some(s => s.p1 || s.p2);
    if (hasScore && !winnerId) {
      toast.error('Por favor, selecione o vencedor.');
      return;
    }

    const formattedScore = hasScore 
      ? scoreSets.filter(s => s.p1 || s.p2).map(s => `${s.p1 || '0'}/${s.p2 || '0'}`).join(' ')
      : '';

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/feed/friendly-match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          opponentId,
          score: formattedScore,
          winnerId: winnerId || null,
          date: date ? new Date(date).toISOString() : null,
          location,
          court
        })
      });
      
      if (res.ok) {
        toast.success(formattedScore ? 'Amistoso lançado com sucesso! Aguardando aprovação.' : 'Amistoso agendado com sucesso!');
        setOpponentId('');
        setScoreSets([{p1: '', p2: ''}]);
        setScoreFormat('1_SET');
        setWinnerId('');
        setDate('');
        setLocation('');
        setCourt('');
        onSuccess();
        onClose();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Erro ao criar amistoso');
      }
    } catch (err) {
      toast.error('Erro de conexão ao criar amistoso');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {
        isOpen && (
          <div 
            style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.7)", backdropFilter: "blur(4px)",
              display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
            }} 
            onClick={onClose}
          >
            <motion.div
              style={{
                background: "#161129",
                width: "100%", maxWidth: "500px", borderRadius: "8px", overflow: "hidden",
                display: "flex", flexDirection: "column", boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                maxHeight: "90vh",
              }}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div style={{ background: "#2b5c3a", padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2 style={{ color: "white", fontSize: "20px", fontWeight: 700, margin: 0 }}>Lançar Amistoso</h2>
                <button onClick={onClose} style={{ background: "none", border: "none", color: "white", cursor: "pointer", display: "flex" }}>
                  <X size={24} />
                </button>
              </div>
              
              <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ position: 'relative' }}>
                  <Search size={20} style={{ position: 'absolute', left: '16px', top: '14px', color: 'rgba(255,255,255,0.4)' }} />
                  <input 
                    type="text" 
                    placeholder="Buscar adversário..." 
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ width: '100%', padding: '12px 16px 12px 48px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
                
                <form id="create-friendly-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                   <div>
                     <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Adversário</label>
                     <select 
                       value={opponentId} 
                       onChange={(e) => setOpponentId(e.target.value)}
                       style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                     >
                       <option value="" style={{color: 'black'}}>-- Selecione --</option>
                       {filteredUsers.map((u: any) => (
                         <option key={u.id} value={u.id} style={{color: 'black'}}>
                           {u.name}
                         </option>
                       ))}
                     </select>
                   </div>
                   
                   <div style={{ display: 'flex', gap: '16px' }}>
                     <div style={{ flex: 1 }}>
                       <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Data</label>
                       <input 
                         type="date"
                         value={date}
                         onChange={(e) => setDate(e.target.value)}
                         style={{ width: '100%', padding: '12px 16px ', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                       />
                     </div>
                   </div>

                   <div style={{ display: 'flex', gap: '16px' }}>
                     <div style={{ flex: 1 }}>
                       <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Local</label>
                       <input 
                         type="text"
                         placeholder="Ex: Clube A"
                         value={location}
                         onChange={(e) => setLocation(e.target.value)}
                         style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                       />
                     </div>
                     <div style={{ flex: 1 }}>
                       <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Quadra</label>
                       <input 
                         type="text"
                         placeholder="Ex: 3"
                         value={court}
                         onChange={(e) => setCourt(e.target.value)}
                         style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                       />
                     </div>
                   </div>

                   <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '8px 0' }} />
                   <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', margin: 0 }}>Opcional: Preencha o placar caso o jogo já tenha acontecido.</p>

                   <div>
                     <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Formato do Jogo</label>
                     <select 
                       value={scoreFormat} 
                       onChange={(e) => setScoreFormat(e.target.value)}
                       style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                     >
                       <option value="1 Set (Padrão)" style={{color: 'black'}}>1 Set (Padrão)</option>
                       <option value="Melhor de 3 Sets" style={{color: 'black'}}>Melhor de 3 Sets</option>
                       <option value="2 Sets + Supertiebreak" style={{color: 'black'}}>2 Sets + Supertiebreak</option>
                       <option value="Melhor de 5 Sets" style={{color: 'black'}}>Melhor de 5 Sets</option>
                     </select>
                   </div>

                   {scoreSets.map((set, idx) => (
                     <div key={idx} style={{ display: 'flex', gap: '16px', alignItems: 'center', marginTop: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>{scoreFormat === '2 Sets + Supertiebreak' && idx === 2 ? 'Seu Supertiebreak' : `Seus Pontos (Set ${idx + 1})`}</label>
                          <input 
                            type="text"
                            placeholder="Ex: 6"
                            value={set.p1}
                            onChange={(e) => {
                              const newSets = [...scoreSets];
                              newSets[idx].p1 = e.target.value;
                              setScoreSets(newSets);
                            }}
                            style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>{scoreFormat === '2 Sets + Supertiebreak' && idx === 2 ? 'Supertiebreak Adv.' : `Pontos Adv. (Set ${idx + 1})`}</label>
                          <input 
                            type="text"
                            placeholder="Ex: 4"
                            value={set.p2}
                            onChange={(e) => {
                              const newSets = [...scoreSets];
                              newSets[idx].p2 = e.target.value;
                              setScoreSets(newSets);
                            }}
                            style={{ width: '100%', padding: '12px 16px ', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>
                     </div>
                   ))}

                   {scoreSets.some(s => s.p1 || s.p2) ? (
                   <div>
                     <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Vencedor</label>
                     <select 
                       value={winnerId} 
                       onChange={(e) => setWinnerId(e.target.value)}
                       style={{ width: '100%', padding: '12px 16px ', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                     >
                       <option value="" style={{color: 'black'}}>-- Selecione --</option>
                       <option value={currentUser?.id} style={{color: 'black'}}>Você</option>
                       {opponentId && (
                         <option value={opponentId} style={{color: 'black'}}>Adversário</option>
                       )}
                     </select>
                   </div>
                   ) : null}

              </form>
            </div>
            
            <div style={{ padding: '20px 24px', background: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
              <button 
                type="button" 
                onClick={onClose} 
                style={{ padding: '10px 20px', background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }}
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                form="create-friendly-form"
                disabled={isSubmitting} 
                style={{ padding: '10px 20px', background: '#2b5c3a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', transition: 'transform 0.2s' }}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar Amistoso'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
